import { Link } from 'react-router-dom';

interface Video {
  id: string;
  title: string;
  category: string;
  categorySlug: string;
  image: string;
  rating: number;
  views?: string;
  year: string;
  type: string;
  episode?: string;
  url: string;
}

interface VideoGridProps {
  title: string;
  subtitle?: string;
  videos: Video[];
  compact?: boolean;
}

export default function VideoGrid({ title, subtitle, videos, compact }: VideoGridProps) {
  return (
    <section className="w-full py-10 md:py-14 px-4 md:px-6">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-end justify-between mb-6 md:mb-8">
          <div>
            <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-900 mb-1">
              {title}
            </h2>
            {subtitle && (
              <p className="text-sm text-foreground-600">{subtitle}</p>
            )}
          </div>
          {videos.length > 0 && videos[0].categorySlug && (
            <Link
              to={`/category/${videos[0].categorySlug}`}
              className="text-sm text-primary-600 hover:text-primary-700 font-medium whitespace-nowrap cursor-pointer"
            >
              查看更多 <i className="ri-arrow-right-s-line"></i>
            </Link>
          )}
        </div>

        {compact ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-4">
            {videos.map((video) => (
              <Link
                key={video.id}
                to={video.url}
                className="group cursor-pointer"
              >
                <div className="relative overflow-hidden rounded-lg aspect-[3/4] mb-2">
                  <img
                    src={video.image}
                    alt={video.title}
                    title={`${video.title} - ${video.category}`}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                  <div className="absolute top-2 left-2">
                    <span className="px-2 py-0.5 rounded text-[10px] font-medium bg-primary-500 text-background-50">
                      {video.rating}
                    </span>
                  </div>
                  {video.episode && (
                    <div className="absolute bottom-2 right-2">
                      <span className="px-2 py-0.5 rounded text-[10px] font-medium bg-black/70 text-white">
                        {video.episode}
                      </span>
                    </div>
                  )}
                </div>
                <h3 className="text-sm font-medium text-foreground-900 line-clamp-1 group-hover:text-primary-600 transition-colors">
                  {video.title}
                </h3>
                <p className="text-xs text-foreground-500 mt-0.5">
                  {video.type} · {video.year}
                </p>
              </Link>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
            {videos.map((video) => (
              <Link
                key={video.id}
                to={video.url}
                className="group cursor-pointer"
              >
                <div className="relative overflow-hidden rounded-lg aspect-video mb-3">
                  <img
                    src={video.image}
                    alt={video.title}
                    title={`${video.title} - ${video.category}`}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                  <div className="absolute top-3 left-3">
                    <span className="px-2 py-0.5 rounded text-xs font-semibold bg-accent-500 text-background-50">
                      {video.rating}
                    </span>
                  </div>
                  <div className="absolute bottom-3 left-3 right-3">
                    <span className="text-xs text-white/80">{video.type} · {video.views}次播放</span>
                  </div>
                </div>
                <h3 className="text-sm font-medium text-foreground-900 line-clamp-1 group-hover:text-primary-600 transition-colors">
                  {video.title}
                </h3>
                <div className="flex items-center gap-2 mt-1">
                  <span className="text-xs text-foreground-500">{video.year}</span>
                  <span className="text-xs text-foreground-500">·</span>
                  <span className="text-xs text-secondary-600">{video.category}</span>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}